import type { GeometryAnnotation, GeometryLabels, TriangleSubtype } from '../types/mathGeometry';

const TRIANGLE_SUBTYPES = new Set<TriangleSubtype>(['right', 'isosceles', 'equilateral', 'scalene']);

/** Vértices en caja normalizada 0–100: v0=inf-izq, v1=inf-der, v2=ápice. */
const NORMALIZED_VERTICES: Record<
  TriangleSubtype,
  { v0: [number, number]; v1: [number, number]; v2: [number, number]; rightAngle?: 'v0' }
> = {
  right: { v0: [10, 90], v1: [90, 90], v2: [10, 10], rightAngle: 'v0' },
  isosceles: { v0: [10, 90], v1: [90, 90], v2: [50, 10] },
  equilateral: { v0: [10, 90], v1: [90, 90], v2: [50, 10] },
  scalene: { v0: [10, 90], v1: [90, 90], v2: [30, 10] },
};

export function normalizeTriangleSubtype(raw: unknown): TriangleSubtype {
  const key = String(raw || '')
    .trim()
    .toLowerCase();
  if (key === 'rectangulo' || key === 'rectángulo' || key === 'rectangular') return 'right';
  if (key === 'isosceles' || key === 'isósceles') return 'isosceles';
  if (key === 'equilateral' || key === 'equilatero' || key === 'equilátero') return 'equilateral';
  if (key === 'scalene' || key === 'escaleno' || key === 'escalène') return 'scalene';
  if (TRIANGLE_SUBTYPES.has(key as TriangleSubtype)) return key as TriangleSubtype;
  return 'scalene';
}

export interface TrianglePoint {
  x: number;
  y: number;
}

function scaleNormalized(nx: number, ny: number, x: number, y: number, w: number, h: number): TrianglePoint {
  return { x: x + (nx / 100) * w, y: y + (ny / 100) * h };
}

export function getTriangleVertices(
  subtype: TriangleSubtype | undefined,
  x: number,
  y: number,
  w: number,
  h: number,
): { v0: TrianglePoint; v1: TrianglePoint; v2: TrianglePoint; subtype: TriangleSubtype } {
  const resolved = subtype && TRIANGLE_SUBTYPES.has(subtype) ? subtype : 'scalene';
  const model = NORMALIZED_VERTICES[resolved];
  return {
    subtype: resolved,
    v0: scaleNormalized(model.v0[0], model.v0[1], x, y, w, h),
    v1: scaleNormalized(model.v1[0], model.v1[1], x, y, w, h),
    v2: scaleNormalized(model.v2[0], model.v2[1], x, y, w, h),
  };
}

/** Path SVG del triángulo escalado al bounding box (x,y,w,h). */
export function getTrianglePath(
  subtype: TriangleSubtype | undefined,
  x: number,
  y: number,
  w: number,
  h: number,
): string {
  const { v0, v1, v2 } = getTriangleVertices(subtype, x, y, w, h);
  return `M ${v0.x},${v0.y} L ${v2.x},${v2.y} L ${v1.x},${v1.y} Z`;
}

function midpoint(a: TrianglePoint, b: TrianglePoint): TrianglePoint {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function outwardOffset(from: TrianglePoint, toward: TrianglePoint, distance: number): TrianglePoint {
  const dx = from.x - toward.x;
  const dy = from.y - toward.y;
  const len = Math.hypot(dx, dy) || 1;
  return {
    x: from.x + (dx / len) * distance,
    y: from.y + (dy / len) * distance,
  };
}

function inwardOffset(vertex: TrianglePoint, centroid: TrianglePoint, distance: number): TrianglePoint {
  const dx = centroid.x - vertex.x;
  const dy = centroid.y - vertex.y;
  const len = Math.hypot(dx, dy) || 1;
  return {
    x: vertex.x + (dx / len) * distance,
    y: vertex.y + (dy / len) * distance,
  };
}

export function getTriangleCentroid(v0: TrianglePoint, v1: TrianglePoint, v2: TrianglePoint): TrianglePoint {
  return { x: (v0.x + v1.x + v2.x) / 3, y: (v0.y + v1.y + v2.y) / 3 };
}

export function getTriangleLabelPositions(
  subtype: TriangleSubtype | undefined,
  x: number,
  y: number,
  w: number,
  h: number,
): { bottom: TrianglePoint; left: TrianglePoint; right: TrianglePoint; center: TrianglePoint } {
  const { v0, v1, v2 } = getTriangleVertices(subtype, x, y, w, h);
  const centroid = getTriangleCentroid(v0, v1, v2);
  const baseMid = midpoint(v0, v1);
  const leftMid = midpoint(v0, v2);
  const rightMid = midpoint(v1, v2);

  return {
    bottom: { x: baseMid.x, y: baseMid.y + 14 },
    left: outwardOffset(leftMid, rightMid, 12),
    right: outwardOffset(rightMid, leftMid, 12),
    center: centroid,
  };
}

export function getTriangleAnnotationPoint(
  subtype: TriangleSubtype | undefined,
  x: number,
  y: number,
  w: number,
  h: number,
  at: string,
): { x: number; y: number; kind: 'vertex' | 'angle' } {
  const { v0, v1, v2, subtype: resolved } = getTriangleVertices(subtype, x, y, w, h);
  const centroid = getTriangleCentroid(v0, v1, v2);
  const anchor = String(at || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');

  const vertexForAnchor = (): TrianglePoint => {
    if (anchor === 'v1' || anchor === 'br' || anchor === 'bottomright') return v1;
    if (anchor === 'v2' || anchor === 'tl' || anchor === 'topleft' || anchor === 'top') return v2;
    if (anchor === 'center') return centroid;
    return v0;
  };

  if (anchor.includes('angle') || anchor.endsWith('-angle')) {
    const vertexKey =
      anchor === 'v1-angle' || anchor === 'br-angle'
        ? 'v1'
        : anchor === 'v2-angle' || anchor === 'top-angle'
          ? 'v2'
          : 'v0';
    const vertex = vertexKey === 'v1' ? v1 : vertexKey === 'v2' ? v2 : v0;
    const inset = inwardOffset(vertex, centroid, Math.min(w, h) * 0.12);
    return { x: inset.x, y: inset.y, kind: 'angle' };
  }

  if (/°|alpha|β|γ/.test(anchor)) {
    const inset = inwardOffset(v0, centroid, Math.min(w, h) * 0.12);
    return { x: inset.x, y: inset.y, kind: 'angle' };
  }

  const vertex = vertexForAnchor();
  const offset = outwardOffset(vertex, centroid, 14);
  return { x: offset.x, y: offset.y, kind: 'vertex' };
}

/** Marcador visual del ángulo recto (triángulo rectángulo). */
export function getRightAngleMarkerPath(
  subtype: TriangleSubtype | undefined,
  x: number,
  y: number,
  w: number,
  h: number,
): string | null {
  const resolved = subtype && TRIANGLE_SUBTYPES.has(subtype) ? subtype : 'scalene';
  if (resolved !== 'right') return null;

  const { v0, v1, v2 } = getTriangleVertices('right', x, y, w, h);
  const size = Math.min(w, h) * 0.1;
  const alongBase = { x: v0.x + (v1.x - v0.x) * (size / Math.hypot(v1.x - v0.x, v1.y - v0.y)), y: v0.y };
  const alongHeight = { x: v0.x, y: v0.y + (v2.y - v0.y) * (size / Math.hypot(v2.x - v0.x, v2.y - v0.y)) };
  const inner = {
    x: alongBase.x + (alongHeight.x - v0.x),
    y: alongBase.y + (alongHeight.y - v0.y),
  };

  return `M ${alongBase.x},${alongBase.y} L ${inner.x},${inner.y} L ${alongHeight.x},${alongHeight.y}`;
}

export function inferTriangleSubtypeFromLabels(labels?: GeometryLabels): TriangleSubtype | undefined {
  const haystack = [labels?.top, labels?.left, labels?.right, labels?.bottom, labels?.center]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (/90\s*°|rect[aá]ngulo|cateto/.test(haystack)) return 'right';
  if (/is[oó]sceles/.test(haystack)) return 'isosceles';
  if (/equil[aá]tero/.test(haystack)) return 'equilateral';
  if (/escaleno/.test(haystack)) return 'scalene';
  return undefined;
}

export function resolveTriangleSubtype(
  explicit: TriangleSubtype | undefined,
  labels?: GeometryLabels,
  annotations?: GeometryAnnotation[],
): TriangleSubtype {
  if (explicit && TRIANGLE_SUBTYPES.has(explicit)) return explicit;

  const fromAnnotations = annotations?.some(
    (a) => a.label.includes('90°') || String(a.at || '').includes('v0-angle'),
  );
  if (fromAnnotations) return 'right';

  return inferTriangleSubtypeFromLabels(labels) ?? 'scalene';
}
